import { OrderOperationType } from 'api/types/entity/account/order';
import {
  TypedForm,
  FormErrors as FormErrorsBase,
  ObjectSchema,
  date,
  number,
  object,
  string,
} from 'libs/form';
import { TFunc } from 'libs/i18n';
import { applySchema } from 'modules/account/common/helpers';

import { testEndDate } from '../../helpers';

import { getSecuritiesDepositSchema } from './schemas/securitiesDepositSchema';
import { getSecuritiesPurchaseSchema } from './schemas/securitiesPurchaseSchema';
import { getSecuritiesSaleSchemas } from './schemas/securitiesSaleSchemas';
import { getSecuritiesWithdrawalSchema } from './schemas/securitiesWithdrawalSchema';
import { OrderFormType } from './type';

export const MAX_FILES = 10;
export const CODE_LENGTH = 6;

const getDefaultSchema = (
  t: TFunc,
  minEndDate: Date | undefined,
  disabledDates: Date[]
) =>
  object({
    startDate: date().required(),
    endDate: date()
      .required()
      .test(
        'endDate',
        t(
          'account.order.sections.executionPeriodSection.fields.endDate.validationMessage'
        ),
        (value, context) =>
          testEndDate(
            context.parent.startDate as Date | null,
            minEndDate,
            value as Date | null,
            disabledDates
          )
      ),

    executionPeriodInBusinessDays: number(),

    otpCode: string().min(CODE_LENGTH),
  });

interface Args {
  orderType?: OrderOperationType;
  minEndDate: Date | undefined;
  disabledDates: Date[];
}

export const getSchema = (
  t: TFunc,
  { disabledDates, minEndDate, orderType }: Args
): ObjectSchema<OrderFormType> =>
  getDefaultSchema(t, minEndDate, disabledDates)
    .concat(
      applySchema(
        orderType === OrderOperationType.SecuritiesSale,
        getSecuritiesSaleSchemas(t)
      )
    )
    .concat(
      applySchema(
        orderType === OrderOperationType.SecuritiesPurchase,
        getSecuritiesPurchaseSchema(t)
      )
    )
    .concat(
      applySchema(
        orderType === OrderOperationType.SecuritiesWithdrawal,
        getSecuritiesWithdrawalSchema(t)
      )
    )
    .concat(
      applySchema(
        orderType === OrderOperationType.SecuritiesDeposit,
        getSecuritiesDepositSchema(t)
      )
    );

export const { Field, Form, useFormContext, useField, Submit } =
  TypedForm<OrderFormType>();

export type FormErrors = FormErrorsBase<OrderFormType>;

export type { OrderFormType };
